import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Table, Form, OverlayTrigger, Tooltip, Dropdown, Button } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';

const ProjectOption = ({ innerProps, label, data }) => (
    <div {...innerProps} className="py-2 px-2 hover-bg-light">
        <div className="fw-bold">{label}</div>
        <div className="small text-muted">{data.details}</div>
    </div>
);

const UserOption = ({ innerProps, label, data }) => (
    <div {...innerProps} className="py-2 px-2 hover-bg-light d-flex align-items-center">
        <div className='c-avatar c-avatar-text c-avatar-sm me-2'>{data.initials}</div>
        <div>{label}</div>
    </div>
);

const TasksTable = ({
    onEdit,
    onMoveOrCopy,
    onLogTime,
    onViewHistory,
    onAddSubtasks,
    onView,
    onDuplicate,
    onDelete,
    onEstimateTime,
    onContactClick,
}) => {
    const [expandedRows, setExpandedRows] = React.useState(new Set());
    const [isAddingTask, setIsAddingTask] = useState(false);
    const [newTask, setNewTask] = useState({
        name: '',
        list: '',
        project: '',
        projectDetails: '',
        members: '',
        startDate: '',
        dueDate: '',
        status: 'Backlog',
        priority: 'Low'
    });
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editingField, setEditingField] = useState(null); // 'name', 'list', or 'members'
    const [editValue, setEditValue] = useState('');
    const [users, setUsers] = useState([
        { value: 'JD', label: 'John Doe', initials: 'JD' },
        { value: 'JS', label: 'Jane Smith', initials: 'JS' },
        { value: 'MP', label: 'Mike Peterson', initials: 'MP' },
        { value: 'AK', label: 'Anna Kim', initials: 'AK' },
        { value: 'RB', label: 'Robert Brown', initials: 'RB' }
    ]);
    const [taskLists, setTaskLists] = useState([
        { value: 'Backend Tasks', label: 'Backend Tasks' },
        { value: 'Frontend Tasks', label: 'Frontend Tasks' },
        { value: 'Design Tasks', label: 'Design Tasks' },
        { value: 'QA Tasks', label: 'QA Tasks' },
        { value: 'DevOps Tasks', label: 'DevOps Tasks' }
    ]);
    const [projects, setProjects] = useState([
        { 
            value: 'Netclues Technologies PVT LTD',
            label: 'Netclues Technologies PVT LTD',
            details: 'Sales: Kartik Mehta | TL: John Smith'
        },
        { 
            value: 'Project Management System',
            label: 'Project Management System',
            details: 'Sales: Mike Ross | TL: Sarah Johnson'
        },
        { 
            value: 'E-commerce Platform',
            label: 'E-commerce Platform',
            details: 'Sales: David Lee | TL: Emily Brown'
        }
    ]);
    const [filteredLists, setFilteredLists] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const editInputRef = useRef(null);
    const selectRef = useRef(null);
    const dropdownRef = useRef(null);

    const handleSave = useCallback(() => {
        if (editingTaskId !== null && editingField) {
            // Here you would typically make an API call to save the changes
            console.log('Saving:', { taskId: editingTaskId, field: editingField, value: editValue });
            
            // Small delay to ensure the UI updates properly
            setTimeout(() => {
                setEditingTaskId(null);
                setEditingField(null);
                setEditValue('');
                setFilteredLists([]);
            }, 100);
        }
    }, [editingTaskId, editingField, editValue]);

    // Handle click outside to save
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (editingTaskId !== null) {
                const selectElement = selectRef.current?.controlRef;
                const selectMenuElement = document.querySelector('.react-select__menu');
                
                if (selectElement && !selectElement.contains(event.target) &&
                    (!selectMenuElement || !selectMenuElement.contains(event.target)) &&
                    (!editInputRef.current || !editInputRef.current.contains(event.target))) {
                    handleSave();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editingTaskId, handleSave]);

    const startEditing = useCallback((taskId, field, currentValue) => {
        setEditingTaskId(taskId);
        setEditingField(field);
        setEditValue(currentValue);
        if (field === 'list') {
            setFilteredLists([...taskLists]); // Show all task lists initially
        }
    }, [taskLists]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingTaskId(null);
            setEditingField(null);
            setEditValue('');
            setFilteredLists([]);
        }
    }, [handleSave]);

    const handleAddTaskClick = () => {
        setIsAddingTask(true);
        setNewTask({ name: '', list: '', project: '', projectDetails: '' });
        setFilteredLists([...taskLists]);
        setFilteredProjects([...projects]);
    };

    const handleNewTaskSave = () => {
        if (newTask.name.trim()) {
            // Here you would typically make an API call to save the new task
            console.log('Saving new task:', newTask);
            setIsAddingTask(false);
            setNewTask({ name: '', list: '' });
            setFilteredLists([]);
        }
    };

    const handleNewTaskKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleNewTaskSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsAddingTask(false);
            setNewTask({ name: '', list: '' });
            setFilteredLists([]);
        }
    };

    const handleListInputChange = (value) => {
        setEditValue(value);
        setFilteredLists(
            taskLists.filter(list => 
                list.toLowerCase().includes(value.toLowerCase())
            )
        );
    };

    const toggleRow = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };
    return (
        <div>
            <Table responsive className='c-table'>
                <thead>
                    <tr>
                        <th width="50px" className='text-center'>
                            <Form.Check />
                        </th>
                    <th>Task</th>
                    <th width="100px" className='text-center'>Assign</th>
                    <th width="120px" className='text-center'>Start Date</th>
                    <th width="120px" className='text-center'>Due Date</th>
                    <th width="140px" className='text-center'>Workflow Status</th>
                    <th width="100px" className='text-center'>Priority</th>
                    <th width="100px" className='text-center'>Estimated</th>
                    <th width="100px" className='text-center'>Logged</th>
                    <th width="250px">Project</th>
                    <th width="50px" className='text-center'>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className='text-center'></td>
                    <td colSpan={10}>
                        <Button variant="link" className='text-decoration-none' onClick={handleAddTaskClick}>
                            <i className="fas fa-plus me-2"></i>Add Task
                        </Button>
                    </td>
                </tr>
                {isAddingTask && (
                    <tr>
                        <td className='text-center'>
                            <Form.Check />
                        </td>
                        <td>
                            <div className='d-flex align-items-center'>
                                <Button variant="link" className='me-3'>
                                    <i className="fas fa-chevron-right"></i>
                                </Button>
                                <div className='d-flex flex-column align-items-start w-100'>
                                    <div className="position-relative w-100 mb-1">
                                        <Select
                                            options={projects}
                                            value={newTask.project ? projects.find(p => p.value === newTask.project) : null}
                                            onChange={(selected) => {
                                                setNewTask({
                                                    ...newTask,
                                                    project: selected.value,
                                                    projectDetails: selected.details
                                                });
                                            }}
                                            placeholder="Select Project"
                                            components={{ Option: ProjectOption }}
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            autoFocus
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '30px',
                                                    height: '30px'
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    height: '30px',
                                                    padding: '0 6px'
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    margin: '0px',
                                                    padding: '0px'
                                                })
                                            }}
                                        />
                                    </div>
                                    
                                    <div className="position-relative w-100 mb-1">
                                        <Select
                                            isMulti
                                            options={taskLists}
                                            value={newTask.list ? newTask.list.split(',').map(v => ({ value: v.trim(), label: v.trim() })) : []}
                                            onChange={(selectedOptions) => {
                                                const value = selectedOptions.map(option => option.value).join(', ');
                                                setNewTask({ ...newTask, list: value });
                                            }}
                                            placeholder="Select Tasklist"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '30px',
                                                    height: '30px'
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    height: '30px',
                                                    padding: '0 6px'
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    margin: '0px',
                                                    padding: '0px'
                                                })
                                            }}
                                        />
                                    </div>

                                    <input
                                        type="text"
                                        className="form-control form-control-xs"
                                        placeholder="Enter Task Name"
                                        value={newTask.name}
                                        onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                                        onKeyDown={handleNewTaskKeyDown}
                                    />
                                </div>
                            </div>
                        </td>
                        <td className='text-center'>
                            <div className='d-flex align-items-center justify-content-center mb-1'>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Add Members</Tooltip>}
                                >
                                    <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                        <i className='fas fa-plus'></i>
                                    </div>
                                </OverlayTrigger>
                            </div>
                        </td>
                        <td className='text-center'>
                            <Flatpickr
                                value={newTask.startDate ? new Date(newTask.startDate) : ''}
                                onChange={([date]) => {
                                    if (date) {
                                        setNewTask({ ...newTask, startDate: date });
                                    }
                                }}
                                options={{
                                    dateFormat: 'd M Y',
                                    enableTime: false,
                                    altInput: true,
                                    altFormat: 'd M Y',
                                    static: true
                                }}
                                className="form-control form-control-xs text-center"
                                placeholder="Start Date"
                            />
                        </td>
                        <td className='text-center'>
                            <Flatpickr
                                value={newTask.dueDate ? new Date(newTask.dueDate) : ''}
                                onChange={([date]) => {
                                    if (date) {
                                        setNewTask({ ...newTask, dueDate: date });
                                    }
                                }}
                                options={{
                                    dateFormat: 'd M Y',
                                    enableTime: false,
                                    altInput: true,
                                    altFormat: 'd M Y',
                                    static: true,
                                    minDate: newTask.startDate ? new Date(newTask.startDate) : null
                                }}
                                className="form-control form-control-xs text-center"
                                placeholder="Due Date"
                            />
                        </td>
                        <td className='text-center'>
                            <Dropdown className='c-dropdown-2'>
                                <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                    Backlog
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <button className="dropdown-item">Backlog</button>
                                    <button className="dropdown-item">Planning</button>
                                    <button className="dropdown-item">In Progress</button>
                                    <button className="dropdown-item">Pending</button>
                                    <button className="dropdown-item">Hold</button>
                                    <button className="dropdown-item">Completed</button>
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                        <td className='text-center'>
                            <Dropdown className='c-dropdown-2'>
                                <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                    Low
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <button className="dropdown-item">Low</button>
                                    <button className="dropdown-item">Medium</button>
                                    <button className="dropdown-item">High</button>
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                        <td className='text-center' onClick={onEstimateTime}></td>
                        <td className='text-center' onClick={onLogTime}></td>
                        <td>
                            
                        </td>
                        <td className='text-center'>
                            <div className='d-flex justify-content-center gap-2'>
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    onClick={handleNewTaskSave}
                                    disabled={!newTask.name.trim()}
                                >
                                    <i className="fas fs-18 fa-check"></i>
                                </Button>
                                <Button 
                                    variant="link text-danger" 
                                    size="sm" 
                                    onClick={() => {
                                        setIsAddingTask(false);
                                        setNewTask({ name: '', list: '' });
                                        setFilteredLists([]);
                                    }}
                                >
                                    <i className="fas fs-18 fa-times"></i>
                                </Button>
                            </div>
                        </td>
                    </tr>
                )}
                {[...Array(5)].map((_, i) => (
                    <React.Fragment key={i}>
                        <tr>
                            <td className='text-center'>
                                <Form.Check />
                            </td>
                            <td>
                                <div className='d-flex align-items-center'>
                                    <Button variant="link" className='me-3' onClick={() => toggleRow(i)}>
                                        <i className={`fas fa-chevron-${expandedRows.has(i) ? 'down' : 'right'}`}></i>
                                    </Button>
                                    <div className='d-flex flex-column align-items-start w-100'>
                                        {editingTaskId === i && editingField === 'name' ? (
                                            <div className='p-tag fw-bold w-100'>
                                                <input
                                                    ref={editInputRef}
                                                    type="text"
                                                    className="form-control form-control-xs fw-bold"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    autoFocus
                                                />
                                            </div>
                                        ) : (
                                            <div 
                                                className='p-tag fw-bold' 
                                                onClick={() => startEditing(i, 'name', 'Home Page')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                Home Page
                                            </div>
                                        )}
                                        
                                        {editingTaskId === i && editingField === 'list' ? (
                                            <div className='position-relative w-100'>
                                                <Select
                                                    ref={selectRef}
                                                    isMulti
                                                    options={taskLists}
                                                    value={editValue ? editValue.split(',').map(v => ({ value: v.trim(), label: v.trim() })) : []}
                                                    onChange={(selectedOptions) => {
                                                        const value = selectedOptions.map(option => option.value).join(', ');
                                                        setEditValue(value);
                                                    }}
                                                    onBlur={handleSave}
                                                    autoFocus
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '30px',
                                                            height: '30px'
                                                        }),
                                                        valueContainer: (base) => ({
                                                            ...base,
                                                            height: '30px',
                                                            padding: '0 6px'
                                                        }),
                                                        input: (base) => ({
                                                            ...base,
                                                            margin: '0px',
                                                            padding: '0px'
                                                        }),
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 9999
                                                        })
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div 
                                                className='p-tag'
                                                onClick={() => {
                                                    const currentLists = 'Backend Tasks'.split(',').map(v => v.trim());
                                                    startEditing(i, 'list', currentLists.join(', '));
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                Backend Tasks
                                            </div>
                                        )}
                                        <div className='p-tag fs-12'>3 subtasks</div>
                                    </div>
                                </div>
                            </td>
                            <td className='text-center'>
                                <div className='d-flex align-items-center justify-content-center mb-1'>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>John Doe</Tooltip>}
                                    >
                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                            JD
                                        </div>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>John Doe</Tooltip>}
                                    >
                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                            JD
                                        </div>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>+3 Members</Tooltip>}
                                    >
                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                            +3
                                        </div>
                                    </OverlayTrigger>
                                </div>
                            </td>
                            <td 
                                className='text-center'
                                onClick={() => startEditing(i, 'startDate', '2025-08-01')}
                                style={{ cursor: 'pointer' }}
                            >
                                {editingTaskId === i && editingField === 'startDate' ? (
                                    <div className='position-relative w-100'>
                                        <Flatpickr
                                            value={editValue}
                                            onChange={([date]) => {
                                                setEditValue(date.toISOString().split('T')[0]);
                                                setTimeout(handleSave, 0);
                                            }}
                                            options={{
                                                dateFormat: 'd M Y',
                                                enableTime: false,
                                                altInput: true,
                                                altFormat: 'd M Y',
                                                static: true
                                            }}
                                            className="form-control form-control-xs text-center"
                                            autoFocus
                                        />
                                    </div>
                                ) : (
                                    '01 Aug 2025'
                                )}
                            </td>
                            <td 
                                className='text-center'
                                onClick={() => startEditing(i, 'dueDate', '2025-08-01')}
                                style={{ cursor: 'pointer' }}
                            >
                                {editingTaskId === i && editingField === 'dueDate' ? (
                                    <div className='position-relative w-100'>
                                        <Flatpickr
                                            value={editValue}
                                            onChange={([date]) => {
                                                setEditValue(date.toISOString().split('T')[0]);
                                                setTimeout(handleSave, 0);
                                            }}
                                            options={{
                                                dateFormat: 'd M Y',
                                                enableTime: false,
                                                altInput: true,
                                                altFormat: 'd M Y',
                                                static: true,
                                                minDate: editingTaskId === i ? 
                                                    document.querySelector(`[data-task-id="${i}"][data-field="startDate"]`)?.value : 
                                                    undefined
                                            }}
                                            className="form-control form-control-xs text-center"
                                            autoFocus
                                        />
                                    </div>
                                ) : (
                                    '01 Aug 2025'
                                )}
                            </td>
                            <td className='text-center'>
                                <Dropdown className='c-dropdown-2'>
                                    <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                        In Progress
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <button className="dropdown-item">Backlog</button>
                                        <button className="dropdown-item">Planning</button>
                                        <button className="dropdown-item">In Progress</button>
                                        <button className="dropdown-item">Pending</button>
                                        <button className="dropdown-item">Hold</button>
                                        <button className="dropdown-item">Completed</button>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                            <td className='text-center'>
                                <Dropdown className='c-dropdown-2'>
                                    <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                        High
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <button className="dropdown-item">Low</button>
                                        <button className="dropdown-item">Medium</button>
                                        <button className="dropdown-item">High</button>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                            <td 
                                className='text-center' 
                                onClick={onEstimateTime}
                            >
                                12h 30m
                            </td>
                            <td className='text-center' onClick={onLogTime}>
                                30m
                            </td>
                            <td>
                                {editingTaskId === i && editingField === 'project' ? (
                                    <div className='position-relative w-100'>
                                        <Select
                                            ref={selectRef}
                                            options={projects}
                                            value={editValue ? projects.find(p => p.value === editValue) : null}
                                            onChange={(selected) => {
                                                setEditValue(selected.value);
                                                setTimeout(handleSave, 0);
                                            }}
                                            components={{ Option: ProjectOption }}
                                            autoFocus
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '30px',
                                                    height: '30px'
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    height: '30px',
                                                    padding: '0 6px'
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    margin: '0px',
                                                    padding: '0px'
                                                }),
                                                menuPortal: (base) => ({
                                                    ...base,
                                                    zIndex: 9999
                                                })
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div onClick={() => {
                                        const currentProject = projects.find(p => p.value === 'Netclues Technologies PVT LTD');
                                        startEditing(i, 'project', currentProject.value);
                                    }}>
                                        <div className='p-tag fw-bold'>Netclues Technologies PVT LTD</div>
                                        <div className='p-tag fs-12'>Sales: Kartik Mehta | TL: John Smith</div>
                                    </div>
                                )}
                            </td>
                            <td className='text-center'>
                                <Dropdown className='c-dropdown-1'>
                                    <Dropdown.Toggle variant="link" id="dropdown-basic">
                                        <i className="fas fa-ellipsis-v"></i>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={onEdit}><i className="fas fa-edit me-2"></i> Edit</Dropdown.Item>
                                        <Dropdown.Item onClick={onMoveOrCopy}><i className="fas fa-copy me-2"></i> Move or Copy</Dropdown.Item>
                                        <Dropdown.Item onClick={onLogTime}><i className="fas fa-clock me-2"></i> Log Time</Dropdown.Item>
                                        <Dropdown.Item onClick={onViewHistory}><i className="fas fa-history me-2"></i> View History</Dropdown.Item>
                                        <Dropdown.Item onClick={onAddSubtasks}><i className="fas fa-plus-circle me-2"></i> Quickly Add Subtasks</Dropdown.Item>
                                        <Dropdown.Item onClick={() => onView(`TASK-${i + 1}`)}><i className="fas fa-eye me-2"></i> View</Dropdown.Item>
                                        <Dropdown.Item onClick={onDuplicate}><i className="fas fa-clone me-2"></i> Duplicate</Dropdown.Item>
                                        <Dropdown.Item onClick={onDelete}><i className="fas fa-trash me-2"></i> Delete</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                        </tr>
                        {expandedRows.has(i) && (
                            <>
                                {[...Array(3)].map((_, subIndex) => (
                                    <React.Fragment key={subIndex}>
                                        <tr className='sub-row'>
                                            <td className='text-center'>
                                                <Form.Check />
                                            </td>
                                            <td>
                                                <div className='p-tag fw-bold'>Sub Task</div>
                                                <div className='p-tag'>Backend Tasks</div>
                                            </td>
                                            <td className='text-center'>
                                                <div className='d-flex align-items-center justify-content-center'>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>John Doe</Tooltip>}
                                                    >
                                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                                            JD
                                                        </div>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>John Doe</Tooltip>}
                                                    >
                                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                                            JD
                                                        </div>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>+3 Members</Tooltip>}
                                                    >
                                                        <div className='c-avatar c-avatar-text c-avatar-sm me-0' onClick={onContactClick}>
                                                            +3
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                            <td 
                                                className='text-center'
                                            >
                                                01 Aug 2025 <br/>at 10:00 AM
                                            </td>
                                            <td 
                                                className='text-center'
                                            >
                                                01 Aug 2025 <br/>at 10:00 AM
                                            </td>
                                            <td className='text-center'>
                                                <Dropdown className='c-dropdown-2'>
                                                    <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                                        In Progress
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <button className="dropdown-item">Completed</button>
                                                        <button className="dropdown-item">In Progress</button>
                                                        <button className="dropdown-item">Planning</button>
                                                        <button className="dropdown-item">Pending</button>
                                                        <button className="dropdown-item">Backlog</button>
                                                        <button className="dropdown-item">Hold</button>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                            <td className='text-center'>
                                                <Dropdown className='c-dropdown-2'>
                                                    <Dropdown.Toggle variant="btn" id="dropdown-basic">
                                                        High
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <button className="dropdown-item">High</button>
                                                        <button className="dropdown-item">Medium</button>
                                                        <button className="dropdown-item">Low</button>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                            <td 
                                                className='text-center' 
                                                onClick={onEstimateTime}
                                            >
                                                12h 30m
                                            </td>
                                            <td className='text-center' onClick={onLogTime}>
                                                30m
                                            </td>
                                            <td></td>
                                            <td className='text-center'>
                                                <Dropdown className='c-dropdown-1'>
                                                    <Dropdown.Toggle variant="link" id="dropdown-basic">
                                                        <i className="fas fa-ellipsis-v"></i>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={onEdit}><i className="fas fa-edit me-2"></i> Edit</Dropdown.Item>
                                                        <Dropdown.Item onClick={onMoveOrCopy}><i className="fas fa-copy me-2"></i> Move or Copy</Dropdown.Item>
                                                        <Dropdown.Item onClick={onLogTime}><i className="fas fa-clock me-2"></i> Log Time</Dropdown.Item>
                                                        <Dropdown.Item onClick={onViewHistory}><i className="fas fa-history me-2"></i> View History</Dropdown.Item>
                                                        <Dropdown.Item onClick={onAddSubtasks}><i className="fas fa-plus-circle me-2"></i> Quickly Add Subtasks</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => onView(`TASK-${i + 1}-SUB-${subIndex + 1}`)}><i className="fas fa-eye me-2"></i> View</Dropdown.Item>
                                                        <Dropdown.Item onClick={onDuplicate}><i className="fas fa-clone me-2"></i> Duplicate</Dropdown.Item>
                                                        <Dropdown.Item onClick={onDelete}><i className="fas fa-trash me-2"></i> Delete</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </React.Fragment>
                ))}
            </tbody>
        </Table>
    </div>
    );
};

export default TasksTable;
